import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
   Dropdown,
   DropdownMenu,
   DropdownToggle
} from "Components/ReactstrapSlim";
import { DateOnly } from "typings/DateTime";
import classes from "./DetailsDropdown.module.scss";
import { ActivityReservationInternalClient, ActivityReservationInternalModel, ReservationMethod } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { UserPictureCache } from "Hooks";
import { formatStringDate, getStartOfMonth, getStartOfNextMonth, toDate, toDateOnly } from "Utils/date";
import { userSelector } from "Utils/selectors";
import { useSelector } from "react-redux";
import { AvatarCircleWithCache } from "Components/AvatarCircle";
import { DatePickerInput } from "Components/Inputs";
import LsLoading, { useLsLoading } from "Components/LsLoading";
import { NOT_DELETED_FILTER } from "Utils/ActivityReservationStatusFilterPresets";

const TIME_FORMAT = "h:mm A";

interface DetailsDropdownProps {
   isOpen: boolean,
   toggle: (e: React.SyntheticEvent) => void,
   accountId: Guid,
   venueId: Guid,
   userPictureCache: UserPictureCache,
   width: number,
   height: number,
   onOpenPrebin: (date?: DateOnly) => void
}

const DetailsDropdown: React.FC<DetailsDropdownProps> = ({ isOpen, toggle, accountId, venueId, userPictureCache, width, height, onOpenPrebin }) => {
   const activityInternalClient = useApiClient(ActivityReservationInternalClient);
   const user = useSelector(userSelector);
   const [selectedDate, setSelectedDate] = useState<Date>(new Date());
   const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
   const [reservationsForMonth, setReservationsForMonth] = useState<ActivityReservationInternalModel[]>([]);
   const [loadingWrapper, isLoading, setIsLoading] = useLsLoading(true);
   const abortControllerRef = useRef<AbortController | undefined>(undefined);
   const selectedDateOnly = toDateOnly(selectedDate);   
   const sameMonth = (date1: Date, date2: Date) => {
      return date1.getMonth() == date2.getMonth() 
         && date1.getFullYear() == date2.getFullYear();
   }
   const reservationRef = useRef<ActivityReservationInternalModel[]>([]);   
   if(sameMonth(selectedDate, selectedMonth) && !isLoading) {
      reservationRef.current = reservationsForMonth.filter(r => toDateOnly(r.startDate) == selectedDateOnly);
   }  
   const reservations = reservationRef.current;
   const bookedDates = reservationsForMonth.map(r => toDate(r.startDate))

   

   const onWorkTogether = (e: React.SyntheticEvent, reservation?: ActivityReservationInternalModel) => {
      e.preventDefault();
      onOpenPrebin(selectedDateOnly);
   }

   const loadReservationsForMonth = async (date: Date, abortSignal?: AbortSignal) => {      
      try {
         setIsLoading(true);         
         const startDate = getStartOfMonth(date);
         const endDate = getStartOfNextMonth(date);
         const data = await activityInternalClient.reservations(
            accountId,
            NOT_DELETED_FILTER,
            undefined,
            undefined,
            toDateOnly(startDate),
            toDateOnly(endDate),
            undefined,
            abortSignal
         );
         if (!abortSignal?.aborted) {
            const result = data.filter(r =>
               r.venueId == venueId
               && r.memberId != user.id
               && r.reservationType != ReservationMethod.Monthly
            );
            setReservationsForMonth(result);
         }
      }
      catch {
         if (!abortSignal?.aborted)
            setReservationsForMonth([]);
      }
      finally {
         if (!abortSignal?.aborted)
            setIsLoading(false);
      }
   }

   const onChange = (newDate: Date) => {
      setSelectedDate(newDate);
   }

   const onMonthChange = async (newDate: Date) => {
      setSelectedMonth(newDate);
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      await loadReservationsForMonth(newDate, abortController.signal);
   }

   useEffect(() => {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      loadReservationsForMonth(selectedDate, abortController.signal);
      return function cancel() {
         abortController.abort();
      }
   }, []);


   return (
      <Dropdown isOpen={isOpen} toggle={toggle}>
         <DropdownToggle style={{ height: `${height}px` }} className={classes.hiddenToggle}></DropdownToggle>
         <DropdownMenu
            modifiers={{
               flip: {
                  enabled: false,
               }
            }}
            className={classes.dropdownMenu}
            style={{
               width: width,
            }}
         >
            <div className={classes.dropdownMenuContainer}>
               <div
                  className={classNames(
                     classes.dropdownMenuContent,
                     classes.dropdownContentContainer,

                  )}
               >
                  <div className={classes.detailsFixed}>
                     <DatePickerInput
                        name="date-picker"
                        onChange={onChange}
                        onMonthChange={onMonthChange}
                        selected={selectedDate}
                        calendarClassName={classes.datePicker}                        
                        highlightDates={[{ [classes.bookedDay]: bookedDates }]}
                        inline
                     />
                  </div>
                  <div className={classNames(classes.detailsFlex)}>
                     {isLoading && sameMonth(selectedDate, selectedMonth)
                        ? <div className={classNames(classes.detailsFlexLoading, loadingWrapper)}>
                           <LsLoading isLoading />
                        </div>
                        : <>
                           <div className={classes.detailsHeader}>{formatStringDate(selectedDateOnly, "dddd, MMM D")}</div>
                           {reservations.length > 0
                              ? <div className={classes.reservationsList}>
                                 {reservations.map(r => {
                                    const [firstName, lastName] = r.memberFullName?.split(" ") ?? [];
                                    return <div key={r.reservationId} className={classes.reservation}>
                                       <div className={classes.reservationLeft}>
                                          <div className={classes.reservationAvatar}>
                                             <AvatarCircleWithCache
                                                key={r.memberEmail}
                                                cache={userPictureCache}
                                                userId={r.memberId}
                                                size="md"
                                                email={r.memberEmail}
                                                firstName={firstName}
                                                lastName={lastName}
                                             />
                                          </div>
                                          <div className={classes.reservationDetails}>
                                             <div className={classes.reservationOwner}>{r.memberFullName}</div>
                                             <div className={classes.reservationTime}>{formatStringDate(r.startDate, TIME_FORMAT)} - {r.endDate ? formatStringDate(r.endDate, TIME_FORMAT, false, true) : "autorenew"}</div>
                                          </div>
                                       </div>
                                       <div className={classes.reservationRight}>
                                          <a href="#" onClick={(e) => { onWorkTogether(e, r) }} className={classes.reservationCta}>Work together</a>
                                       </div>
                                    </div>
                                 })}
                              </div>
                              : <div className={classes.reservationNullState}>
                                 <div className={classes.nullStateTextWrapper}>
                                    <div className={classes.nullStateHeader}>Be the first to book here and invite your coworkers</div>
                                    <div className={classes.nullStateSubheader}>Your coworkers are not here this day.</div>
                                    <a className={classes.nullStateCta} href="#" onClick={(e) => onWorkTogether(e)}>Book space on {formatStringDate(selectedDateOnly, "MMM D")}</a>
                                 </div>
                              </div>}
                        </>}
                  </div>
               </div>
            </div>
         </DropdownMenu>
      </Dropdown>
   );
};

export default DetailsDropdown;
